/**
 * The hands-free interview loop.
 *
 * Ties the three pieces together — {@link MicCapture}, {@link ScribeSocket},
 * {@link Speaker} — into one cycle:
 *
 *   listening → (VAD ends the utterance) → thinking → speaking → listening
 *
 * and holds the invariant that makes it survive a car: **the assistant and the
 * driver never have the floor at once**.
 *
 * ## Why half-duplex
 *
 * The obvious design keeps the mic open while the assistant talks so the user
 * can interrupt. In a car that fails immediately: the reply comes out of the
 * cabin speakers, back into the same Bluetooth mic, and gets transcribed as the
 * user's next answer. The interview then answers itself. Browser echo
 * cancellation is tuned for a laptop's speaker-to-mic path, not a car's, and
 * over a Bluetooth hands-free link the AEC is in the headset's firmware where
 * we have no say at all.
 *
 * So the mic is muted for exactly as long as the assistant is speaking. That
 * costs the ability to talk over it, which is why {@link barge} exists and why
 * it's wired to the Media Session controls — the steering-wheel or dash
 * play/pause button becomes "stop talking and listen to me". That's a button a
 * driver can already find without looking, which no on-screen control is.
 *
 * The session is intentionally a ref-held machine rather than React state: the
 * audio callbacks fire far faster than a render, and every one of them needs
 * the *current* mic, socket, and speaker, not the ones from a closure.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { MicCapture, MicCaptureError } from "@/lib/voice/capture";
import { ScribeSocket } from "@/lib/voice/scribe";
import { Speaker } from "@/lib/voice/speaker";
import type { VoiceTransport } from "@/lib/useVoice";

export type VoicePhase =
  /** Not running. */
  | "idle"
  /** Acquiring mic, token, and socket. */
  | "starting"
  /** Mic live, waiting for the user to speak (or finish speaking). */
  | "listening"
  /** Utterance submitted; waiting on the interview turn. */
  | "thinking"
  /** Reply playing; mic muted. */
  | "speaking"
  /** Stopped by a failure. `error` says what. */
  | "error";

/**
 * Submits one user utterance and returns what to say back.
 *
 * Returning `done: true` ends the session — the topic closed itself and there's
 * nothing left to ask. Returning `null` means the turn produced nothing to
 * speak, and the loop just reopens the mic.
 */
export type UtteranceHandler = (
  text: string,
) => Promise<{ reply: string; done: boolean } | null>;

export interface VoiceSession {
  phase: VoicePhase;
  /** Live transcription of what's being said right now. Unstable — display only. */
  partial: string;
  /** What the assistant last said, for the on-screen caption. */
  lastReply: string;
  error: string | null;
  start: () => void;
  stop: () => void;
  /** Cut the assistant off and listen. No-op unless it's speaking. */
  barge: () => void;
}

/**
 * How long to wait after a committed utterance for another one before
 * submitting.
 *
 * The server already waits out a 1.2s pause before committing, so this isn't
 * about detecting the end of speech — it's about *stitching*. A driver who
 * pauses at a junction mid-answer produces two commits a second apart, and
 * submitting the first alone would answer half a thought. Long enough to catch
 * the continuation, short enough not to feel like lag on a normal answer.
 */
const UTTERANCE_GRACE_MS = 700;

export function useVoiceSession(
  transport: VoiceTransport,
  sampleRate: number,
  onUtterance: UtteranceHandler,
  /**
   * Text to speak once the pipeline is up, before the first listen — normally
   * the question already on screen.
   *
   * Without it, tapping "Answer by voice" produces silence and the user has to
   * guess whether it worked and what they're supposed to be answering. Hearing
   * the current question read back is both the confirmation and the prompt.
   */
  openingLine?: () => string | null,
): VoiceSession {
  const [phase, setPhase] = useState<VoicePhase>("idle");
  const [partial, setPartial] = useState("");
  const [lastReply, setLastReply] = useState("");
  const [error, setError] = useState<string | null>(null);

  const micRef = useRef<MicCapture | null>(null);
  const socketRef = useRef<ScribeSocket | null>(null);
  const speakerRef = useRef<Speaker | null>(null);
  const wakeLockRef = useRef<WakeLockSentinel | null>(null);
  /** Committed utterances waiting on the grace window to close. */
  const pendingRef = useRef<string[]>([]);
  const graceTimerRef = useRef<number | null>(null);
  /**
   * Mirrors `phase` for the audio callbacks, which run between renders and
   * would otherwise read a stale value from their closure.
   */
  const phaseRef = useRef<VoicePhase>("idle");
  /** Guards every async continuation against a session the user already stopped. */
  const runIdRef = useRef(0);

  // `onUtterance` is rebuilt every render by its caller (it closes over a
  // mutation). Held in a ref so the audio pipeline isn't torn down and rebuilt
  // — mid-sentence — on an unrelated re-render.
  const utteranceRef = useRef(onUtterance);
  utteranceRef.current = onUtterance;
  const openingRef = useRef(openingLine);
  openingRef.current = openingLine;

  const setPhaseBoth = useCallback((next: VoicePhase) => {
    phaseRef.current = next;
    setPhase(next);
  }, []);

  const releaseWakeLock = useCallback(() => {
    void wakeLockRef.current?.release().catch(() => {});
    wakeLockRef.current = null;
  }, []);

  const teardown = useCallback(() => {
    runIdRef.current += 1;
    if (graceTimerRef.current !== null) {
      window.clearTimeout(graceTimerRef.current);
      graceTimerRef.current = null;
    }
    pendingRef.current = [];
    socketRef.current?.close();
    socketRef.current = null;
    micRef.current?.stop();
    micRef.current = null;
    speakerRef.current?.dispose();
    speakerRef.current = null;
    releaseWakeLock();
    setPartial("");
  }, [releaseWakeLock]);

  const fail = useCallback(
    (message: string) => {
      teardown();
      setError(message);
      setPhaseBoth("error");
    },
    [setPhaseBoth, teardown],
  );

  const stop = useCallback(() => {
    teardown();
    setError(null);
    setPhaseBoth("idle");
  }, [setPhaseBoth, teardown]);

  /** Reopen the mic for the user's next answer. */
  const listen = useCallback(() => {
    micRef.current?.setMuted(false);
    setPartial("");
    setPhaseBoth("listening");
  }, [setPhaseBoth]);

  /**
   * Run one full turn: submit what was heard, speak the reply, hand the floor
   * back. The mic stays muted throughout — see the half-duplex note above.
   */
  const runTurn = useCallback(
    async (text: string) => {
      const runId = runIdRef.current;
      micRef.current?.setMuted(true);
      setPartial("");
      setPhaseBoth("thinking");

      let result: Awaited<ReturnType<UtteranceHandler>>;
      try {
        result = await utteranceRef.current(text);
      } catch {
        // The turn failed, but the session hasn't: say so out loud and reopen
        // the mic. Dropping a driver into a silent, dead page is the one
        // outcome worth going out of the way to avoid.
        if (runId !== runIdRef.current) return;
        const apology = "Sorry — I didn't catch that one. Could you say it again?";
        setLastReply(apology);
        try {
          await speakerRef.current?.speak(apology);
        } catch {
          // Playback is broken too; the caption still carries the message.
        }
        if (runId !== runIdRef.current) return;
        listen();
        return;
      }
      if (runId !== runIdRef.current) return;

      if (!result) {
        listen();
        return;
      }

      setLastReply(result.reply);
      setPhaseBoth("speaking");
      try {
        await speakerRef.current?.speak(result.reply);
      } catch (err) {
        if (runId !== runIdRef.current) return;
        fail(err instanceof Error ? err.message : "Couldn't play the reply.");
        return;
      }
      if (runId !== runIdRef.current) return;

      if (result.done) {
        // The interviewer closed the topic. Ending the session here releases
        // the mic — and lets the Bluetooth device drop back to full-quality
        // A2DP — rather than leaving it open on a finished conversation.
        stop();
        return;
      }
      listen();
    },
    [fail, listen, setPhaseBoth, stop],
  );

  /** Close the grace window and submit everything heard within it as one answer. */
  const flushUtterance = useCallback(() => {
    graceTimerRef.current = null;
    const text = pendingRef.current.join(" ").trim();
    pendingRef.current = [];
    if (!text) {
      listen();
      return;
    }
    void runTurn(text);
  }, [listen, runTurn]);

  const handleCommitted = useCallback(
    (text: string) => {
      // Only the user's own turn counts. Anything transcribed while the
      // assistant holds the floor is echo or road noise — the mic is muted, so
      // this should be unreachable, but a stray commit slipping through here
      // would be indistinguishable from the user answering their own question.
      if (phaseRef.current !== "listening") return;
      pendingRef.current.push(text);
      if (graceTimerRef.current !== null) window.clearTimeout(graceTimerRef.current);
      graceTimerRef.current = window.setTimeout(flushUtterance, UTTERANCE_GRACE_MS);
    },
    [flushUtterance],
  );

  const barge = useCallback(() => {
    if (phaseRef.current !== "speaking") return;
    // `stop()` settles the promise `runTurn` is awaiting, so the loop continues
    // from there and reopens the mic on its own.
    speakerRef.current?.stop();
  }, []);

  const start = useCallback(() => {
    if (phaseRef.current !== "idle" && phaseRef.current !== "error") return;
    setError(null);
    setLastReply("");
    setPhaseBoth("starting");
    const runId = ++runIdRef.current;

    // Created and unlocked synchronously in the tap that called `start` — iOS
    // only grants audio playback to a gesture, and by the time there's a reply
    // to play the gesture is long gone.
    const speaker = new Speaker(transport.fetchSpeech);
    speakerRef.current = speaker;
    void speaker.unlock();

    void (async () => {
      try {
        const mic = await MicCapture.start(sampleRate, (chunk) => {
          socketRef.current?.send(chunk);
        });
        if (runId !== runIdRef.current) {
          mic.stop();
          return;
        }
        // Muted until `listen()` grants the floor. The worklet still runs, so
        // the socket sees a steady stream of (silent) audio and never looks
        // idle while we're connecting or reading the opening line.
        mic.setMuted(true);
        micRef.current = mic;

        const socket = new ScribeSocket(transport.mintSocketUrl, sampleRate, {
          onPartial: (text) => {
            if (phaseRef.current === "listening") setPartial(text);
          },
          onCommitted: handleCommitted,
          onError: (message) => {
            if (runId === runIdRef.current) fail(message);
          },
        });
        socketRef.current = socket;
        await socket.connect();
        if (runId !== runIdRef.current) return;

        // Best-effort: a locked screen suspends the audio graph and silently
        // ends the interview. Not supported everywhere, and never fatal.
        try {
          wakeLockRef.current = (await navigator.wakeLock?.request("screen")) ?? null;
        } catch {
          // Denied or unsupported — the session runs, the screen may just dim.
        }
        if (runId !== runIdRef.current) {
          releaseWakeLock();
          return;
        }

        // Read the question already on screen before opening the mic, so the
        // first thing the user hears confirms voice is working and tells them
        // what they're answering.
        const opening = openingRef.current?.();
        if (opening) {
          setLastReply(opening);
          setPhaseBoth("speaking");
          try {
            await speakerRef.current?.speak(opening);
          } catch {
            // Playback failed but capture is fine — a usable session where the
            // user reads the question instead of hearing it beats no session.
          }
          if (runId !== runIdRef.current) return;
        }

        listen();
      } catch (err) {
        if (runId !== runIdRef.current) return;
        fail(
          err instanceof MicCaptureError
            ? err.message
            : "Couldn't start voice. Check your microphone and connection.",
        );
      }
    })();
  }, [fail, handleCommitted, listen, releaseWakeLock, sampleRate, setPhaseBoth, transport]);

  // Media Session: map the car's own transport controls onto the conversation.
  // While the assistant is talking these are the only controls a driver can
  // operate without looking at the screen, so play/pause/stop all mean the same
  // thing here — "my turn". Registered only while speaking, and cleared after,
  // so the page doesn't hijack media keys during the rest of the session.
  useEffect(() => {
    if (phase !== "speaking" || !("mediaSession" in navigator)) return;
    const handler = () => barge();
    const actions: MediaSessionAction[] = ["pause", "stop", "nexttrack"];
    actions.forEach((action) => {
      try {
        navigator.mediaSession.setActionHandler(action, handler);
      } catch {
        // Older browsers reject unknown actions; the rest still register.
      }
    });
    return () => {
      actions.forEach((action) => {
        try {
          navigator.mediaSession.setActionHandler(action, null);
        } catch {
          // Ignore — nothing to clean up if it never registered.
        }
      });
    };
  }, [barge, phase]);

  // The screen wake lock is dropped by the browser whenever the page is
  // backgrounded (a navigation app coming to the front, a phone call). Retake
  // it on return, or the interview dies the second time the screen sleeps.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      if (phaseRef.current === "idle" || phaseRef.current === "error") return;
      if (wakeLockRef.current) return;
      // The request is async, and the user can stop the session while it's in
      // flight. Without this generation check the callback would assign a lock
      // that `teardown` has already run past, holding the screen awake on a
      // finished interview until the browser eventually reclaims it.
      const runId = runIdRef.current;
      void navigator.wakeLock
        ?.request("screen")
        .then((lock) => {
          if (runId !== runIdRef.current) {
            void lock.release().catch(() => {});
            return;
          }
          wakeLockRef.current = lock;
        })
        .catch(() => {});
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, []);

  // Release the mic (and the Bluetooth HFP route) if the page unmounts
  // mid-session — navigating away must not leave the device in call mode.
  useEffect(() => teardown, [teardown]);

  return { phase, partial, lastReply, error, start, stop, barge };
}
